import { G } from '@mobily/ts-belt';
import AddIcon from '@mui/icons-material/Add';
import { Dialog, DialogActions, DialogContent, Typography } from '@mui/material';
import { CreateInDialogButtonProps } from '@react-admin/ra-form-layout';
import { useCallback, useMemo, useState } from 'react';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- no permissions needed
import { Button, useGetList, useNotify, useRefresh, useStore, useTranslate } from 'react-admin';
import { FieldValues } from 'react-hook-form';
import { match } from 'ts-pattern';

import {
  B2bCompanyPaymentMethod,
  ExternalBankAccountType,
  PostB2bCompaniesPaymentMethodBody,
} from '@Common/types/bo/b2b/companies';
import { ZodUkAccountNumber, ZodUkSortCode } from '@Common/types/zod/bank_account';

import {
  CreateInDialogButton,
  DialogTitleWithX,
  StepperForm,
  StepperFormStep,
} from '@sh4res/bo-common/components';
import { PaymentDetailsStep } from '@sh4res/bo-common/components/B2bCompanyPaymentMethods/company_payment_methods/steps/PaymentDetailsStep';
import { PaymentMethodStep } from '@sh4res/bo-common/components/B2bCompanyPaymentMethods/company_payment_methods/steps/PaymentMethodStep';
import { SignatureStep } from '@sh4res/bo-common/components/B2bCompanyPaymentMethods/company_payment_methods/steps/SignatureStep';
import { useResourceBasePath, useUploadPresignedUrl } from '@sh4res/bo-common/hooks';
import { getErrorMessage } from '@sh4res/bo-common/utils';
import { isAxaEnv } from '@sh4res/bo-common/utils/env';

const steps: (
  companyName: string,
  companyId: number,
  paymentMethods: B2bCompanyPaymentMethod[] | undefined,
  hasCcb: boolean,
) => StepperFormStep[] = (companyName, companyId, paymentMethods, hasCcb) => [
  {
    label: 'resources.bo_common.b2b.companies.payment_methods.sections.payment_method.title',
    content: <PaymentMethodStep paymentMethods={paymentMethods} hasCcb={hasCcb} />,
  },
  {
    label: 'resources.bo_common.b2b.companies.payment_methods.sections.details.title',
    content: <PaymentDetailsStep companyName={companyName} />,
  },
  {
    label: 'resources.bo_common.b2b.companies.payment_methods.sections.signature.title',
    content: <SignatureStep companyId={companyId} />,
    locked: (values: FieldValues) =>
      G.isNotNullable(values.payment_method_type) &&
      values.payment_method_type !== 'sepa_direct_debit',
  },
];

export const B2bCompaniesPaymentMethodCreate = ({
  record,
  companyName,
  hasCcb: companyHasCcb,
  ...props
}: CreateInDialogButtonProps['ButtonProps'] & {
  companyName: string;
  record: { company_id: number };
  hasCcb: boolean;
}) => {
  // ccb_withdrawal payment methods are not available on AXA prod yet
  const hasCcb = companyHasCcb && !isAxaEnv();
  const resourceBasePath = useResourceBasePath();
  const notify = useNotify();
  const refresh = useRefresh();
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const { upload } = useUploadPresignedUrl();

  const { data: paymentMethods } = useGetList<B2bCompanyPaymentMethod>(
    `${resourceBasePath}companies/payment_methods`,
    {
      filter: { b2b_company_id: record.company_id },
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'id', order: 'ASC' },
    },
  );

  const [storedFormData, setStoredFormData] = useStore(
    'b2b/companies.payment_methods.create.form_data',
  );

  const initialFormData = useMemo<FieldValues>(
    () => ({
      payment_method_intent: 'principal',
      ...(storedFormData as FieldValues | undefined),
      ...(hasCcb ? {} : { payment_method_intent: 'principal' }),
    }),
    [storedFormData, hasCcb],
  );

  const onConfirmationModalClose = useCallback(() => {
    setIsConfirmationModalOpen(false);
    setStoredFormData(undefined);
  }, [setIsConfirmationModalOpen, setStoredFormData]);

  const onError = useCallback(
    (error: unknown) => {
      notify('resources.bo_common.b2b.companies.payment_methods.actions.create.failure', {
        type: 'warning',
        messageArgs: { error: getErrorMessage(error) },
        multiLine: true,
      });
      setStoredFormData(undefined);
    },
    [notify, setStoredFormData],
  );

  const onSuccess = useCallback(() => {
    setIsConfirmationModalOpen(true);
    refresh();
  }, [setIsConfirmationModalOpen, refresh]);

  const uploadRib = useCallback(
    async (
      ribFile: { rawFile?: File; title?: string } | undefined,
    ): Promise<{ presigned_path: string | null; document_name: string | null }> => {
      if (!ribFile || !ribFile.rawFile || !(ribFile.rawFile instanceof File)) {
        throw new Error('RIB document is required');
      }

      const uploadResult = await upload(ribFile.rawFile);
      if (!uploadResult) {
        throw new Error('Error during upload');
      }

      return {
        presigned_path: uploadResult.path,
        document_name: ribFile.title || null,
      };
    },
    [upload],
  );

  const [confirmationVariant, setConfirmationVariant] = useState<
    'sepa_direct_debit' | 'sepa_direct_debit_external_signatory' | 'external_bank'
  >('sepa_direct_debit');

  const handleTransform = useCallback(
    async (data: FieldValues): Promise<PostB2bCompaniesPaymentMethodBody | undefined> => {
      const {
        payment_method_type,
        payment_method_intent: intent,
        iban,
        bic,
        account_type,
        account_number,
        sort_code,
        rib,
        is_sepa_mandate_signed,
        signature_delegation,
        external_signatory_firstname,
        external_signatory_lastname,
        external_signatory_email,
      } = data;

      const { presigned_path, document_name } = await uploadRib(rib);

      const isSepaDirectDebit = payment_method_type === 'sepa_direct_debit';
      const isExternalSignatoryFlow =
        isSepaDirectDebit && signature_delegation === 'external_signatory';

      if (isExternalSignatoryFlow) {
        setConfirmationVariant('sepa_direct_debit_external_signatory');
      } else if (isSepaDirectDebit) {
        setConfirmationVariant('sepa_direct_debit');
      } else {
        setConfirmationVariant('external_bank');
      }

      const commonFields = {
        company_id: record.company_id,
        presigned_path,
        document_name,
      };

      if (isSepaDirectDebit) {
        return {
          type: 'sepa_direct_debit',
          bic,
          payment_type: 'recurring',
          intent,
          iban: iban.replaceAll(' ', ''),
          is_sepa_mandate_signed: !isExternalSignatoryFlow ? is_sepa_mandate_signed : undefined,
          external_signatory: isExternalSignatoryFlow
            ? {
                firstname: external_signatory_firstname,
                lastname: external_signatory_lastname,
                email: external_signatory_email,
              }
            : undefined,
          ...commonFields,
        };
      }

      const externalBankAccountType = (account_type ?? 'iban') as ExternalBankAccountType;

      return match(externalBankAccountType)
        .with('iban', () => ({
          type: 'external_bank' as const,
          account_type: 'iban' as const,
          intent,
          iban: iban.replaceAll(' ', ''),
          bic,
          ...commonFields,
        }))
        .with('intl', () => ({
          type: 'external_bank' as const,
          account_type: 'intl' as const,
          intent,
          account_number,
          bic,
          ...commonFields,
        }))
        .with('scan', () => ({
          type: 'external_bank' as const,
          account_type: 'scan' as const,
          intent,
          sort_code: ZodUkSortCode.parse(sort_code),
          account_number: ZodUkAccountNumber.parse(account_number),
          bic,
          ...commonFields,
        }))
        .exhaustive();
    },
    [record.company_id, uploadRib, setConfirmationVariant],
  );

  return (
    <>
      <CreateInDialogButton
        testId="BoExternalB2bCompanyPaymentMethodCreate_CreateButton"
        resource={`${resourceBasePath}companies/payment_methods`}
        fullWidth
        maxWidth="lg"
        title="resources.bo_common.b2b.companies.payment_methods.create.title"
        ButtonProps={{
          variant: 'contained',
          color: 'info',
          size: 'small',
          sx: {
            fontWeight: 'bold',
          },
          ...props,
        }}
        icon={<AddIcon />}
        transform={handleTransform}
        mutationOptions={{ onSuccess, onError }}
        disableEscapeKeyDown
        sx={{
          '& .MuiDialog-paper': {
            height: '100%',
          },
          '& form': {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          },
        }}
        record={initialFormData}>
        <StepperForm
          steps={steps(companyName, record.company_id, paymentMethods, hasCcb)}
          sx={{
            flexGrow: 1,
            '& > div': {
              height: '100%',
            },
          }}
          permissions={{
            resource: `${resourceBasePath}companies/payment_methods`,
            action: 'create',
          }}
          testId="BoExternalB2bCompanyPaymentMethodCreate"
        />
      </CreateInDialogButton>
      <SuccessModal
        isOpen={isConfirmationModalOpen}
        onClose={onConfirmationModalClose}
        variant={confirmationVariant}
      />
    </>
  );
};

const SuccessModal = ({
  isOpen,
  onClose,
  variant,
}: {
  isOpen: boolean;
  onClose: () => void;
  variant: 'sepa_direct_debit' | 'sepa_direct_debit_external_signatory' | 'external_bank';
}) => {
  const translate = useTranslate();
  const methodType = variant;

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithX onClose={onClose}>
        {translate(
          `resources.bo_common.b2b.companies.payment_methods.sections.success_modal.${methodType}.title`,
        )}
      </DialogTitleWithX>

      <DialogContent>
        <Typography variant="body1">
          {translate(
            `resources.bo_common.b2b.companies.payment_methods.sections.success_modal.${methodType}.body`,
          )}
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          onClick={onClose}
          label={`resources.bo_common.b2b.companies.payment_methods.sections.success_modal.${methodType}.btn`}
          data-testid="BoExternalB2bCompanyPaymentMethodCreate_SuccessModalCloseButton"
        />
      </DialogActions>
    </Dialog>
  );
};
